/**
 * Importação do extrato de pedágio (Sem Parar).
 *
 * Fluxo em duas etapas de propósito: o gestor sobe o PDF, vê a PRÉVIA — período
 * real, placas, valores, quais placas não estão no cadastro — e só então
 * confirma. Import de custo é irreversível na percepção de quem usa; descobrir
 * o que entrou depois de gravar é tarde.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { ChartCard } from "../components/ChartCard";
import { KpiTile } from "../components/intelligence/KpiTile";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import { IconDocument, IconDownload, IconCheck, IconAlertTriangle, IconInfo } from "../components/icons";

interface ItemPrevia {
  plate: string;
  tolls: number;
  tollCount: number;
  total: number;
  cadastrada: boolean;
}

interface Previa {
  ok: boolean;
  problemas: string[];
  invoiceNumber: string;
  substituiu: boolean;
  periodStart: string | null;
  periodEnd: string | null;
  subtotal: number | null;
  total: number | null;
  placas: number;
  passagens: number;
  placasSemCadastro: string[];
  itens: ItemPrevia[];
}

interface FaturaImportada {
  id: string;
  invoiceNumber: string;
  source: string;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  subtotal: number | null;
  credits: number | null;
  total: number | null;
  fileName: string | null;
  importedAt: string;
  plates: number;
}

const brl = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—";

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
/** "2026-10-10" → "10/out" (eixo do gráfico: faturas vencem várias vezes no mês). */
const diaMes = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${MES[d.getUTCMonth()]}`;
};

/**
 * Diferença entre o Total e (Subtotal − Créditos). Na maioria das faturas fecha
 * em zero; quando não fecha, são outros lançamentos da própria fatura (a tela
 * mostra em vez de deixar a conta parecer errada).
 */
const outrosLancamentos = (f: FaturaImportada) =>
  f.subtotal == null || f.total == null ? 0 : Math.round((f.total - (f.subtotal - (f.credits ?? 0))) * 100) / 100;

/** Estatística compacta da prévia do extrato. */
function PreviaStat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "warning" }) {
  return (
    <div className={`rounded-lg px-4 py-3 ${tone === "warning" ? "bg-warning-50" : "bg-surface-muted"}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${tone === "warning" ? "text-warning-700" : "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 leading-snug">{sub}</p>}
    </div>
  );
}

export default function TollImportPage() {
  const qc = useQueryClient();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const { data: faturas, isLoading: carregandoFaturas } = useQuery({
    queryKey: ["tolls", "invoices"],
    queryFn: () => api.get<{ invoices: FaturaImportada[] }>("/tolls/invoices"),
  });

  const analisar = useMutation({
    mutationFn: (f: File) => api.upload<Previa>("/tolls/analyze", f),
    onSuccess: (r) => { setPrevia(r); setErro(null); },
    onError: (e: ApiError) => { setPrevia(null); setErro(e.message); },
  });

  const importar = useMutation({
    mutationFn: (f: File) => api.upload<Previa>("/tolls/import", f),
    onSuccess: (r) => {
      setPrevia(r);
      setErro(null);
      void qc.invalidateQueries({ queryKey: ["tolls", "invoices"] });
      // O raio-X mostra o pedágio pago; sem invalidar, ele seguiria com o
      // número de antes do import.
      void qc.invalidateQueries({ queryKey: ["transport", "vehicle-drilldown"] });
    },
    onError: (e: ApiError) => {
      const d = e.details as Previa | undefined;
      if (d?.problemas?.length) { setPrevia(d); setErro(null); }
      else setErro(e.message);
    },
  });

  function escolher(f: File | null) {
    setArquivo(f);
    setPrevia(null);
    setErro(null);
    importar.reset();
    if (f) analisar.mutate(f);
  }

  const ocupado = analisar.isPending || importar.isPending;
  const lista = faturas?.invoices ?? [];
  const faturaList = useProgressiveList(lista);

  // Resumo das faturas importadas. Esta tela não usa o filtro de período do
  // topo: é o histórico de extratos, e a margem por placa é quem distribui o
  // pedágio pago mês a mês (pelas passagens datadas).
  const resumo = useMemo(() => {
    if (lista.length === 0) return null;
    const total = lista.reduce((s, f) => s + (f.total ?? 0), 0);
    const subtotal = lista.reduce((s, f) => s + (f.subtotal ?? 0), 0);
    const creditos = lista.reduce((s, f) => s + (f.credits ?? 0), 0);
    const cobreAte = lista.map((f) => f.periodEnd).filter(Boolean).sort().at(-1) ?? null;
    const ultimaImport = lista.map((f) => f.importedAt).sort().at(-1) ?? null;
    const mediaPlacas = Math.round(lista.reduce((s, f) => s + f.plates, 0) / lista.length);
    return { total, subtotal, creditos, cobreAte, ultimaImport, mediaPlacas };
  }, [lista]);

  // Gráfico em ordem cronológica (a lista vem da mais recente para a mais antiga).
  const serie = useMemo(
    () => [...lista].filter((f) => f.dueDate).sort((a, b) => (a.dueDate! < b.dueDate! ? -1 : 1))
      .map((f) => ({ venc: diaMes(f.dueDate), pago: f.total ?? 0, creditos: f.credits ?? 0, fatura: f.invoiceNumber })),
    [lista],
  );

  // Etapa atual do fluxo (só visual): 1 escolher → 2 conferir → 3 confirmado.
  const etapa = importar.isSuccess ? 3 : previa && previa.problemas.length === 0 ? 2 : 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Pedágio"
        subtitle="Extrato Sem Parar: o valor pago na praça, para auditar contra o pedágio cobrado no frete"
      />

      {/* Resumo do histórico importado */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Faturas importadas" value={resumo ? String(lista.length) : "—"}
          footnote={resumo ? `Em média ${resumo.mediaPlacas} placas por fatura` : "Nenhum extrato ainda"} loading={carregandoFaturas} />
        <KpiTile label="Total pago nas faturas" value={resumo ? brl(resumo.total) : "—"}
          footnote={resumo ? `Soma das ${lista.length} faturas, já com os créditos abatidos` : undefined} loading={carregandoFaturas} />
        <KpiTile label="Créditos abatidos" value={resumo ? brl(resumo.creditos) : "—"}
          footnote={resumo && resumo.subtotal > 0
            ? `${((resumo.creditos / resumo.subtotal) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do subtotal das faturas`
            : undefined}
          loading={carregandoFaturas} />
        <KpiTile label="Extrato cobre até" value={resumo?.cobreAte ? dia(resumo.cobreAte) : "—"}
          footnote={resumo?.ultimaImport
            ? `Última importação em ${new Date(resumo.ultimaImport).toLocaleDateString("pt-BR")}`
            : undefined}
          loading={carregandoFaturas} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Importar */}
        <div className="card overflow-hidden xl:col-span-2 flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Importar extrato</p>
            <p className="text-xs text-gray-400 mt-0.5">PDF baixado no portal Sem Parar Empresas</p>
          </div>
          <div className="p-5 space-y-4 flex-1">
            <ol className="flex items-center gap-2 text-xs">
              {["Escolher o PDF", "Conferir a prévia", "Confirmar"].map((t, i) => {
                const n = i + 1;
                const feito = etapa > n || (n === 3 && etapa === 3);
                const atual = etapa === n && !feito;
                return (
                  <li key={t} className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                      feito ? "bg-tm-500 text-white" : atual ? "bg-tm-100 text-tm-700 ring-1 ring-tm-400" : "bg-surface-muted text-gray-400"}`}>
                      {feito ? <IconCheck className="w-3 h-3" /> : n}
                    </span>
                    <span className={atual || feito ? "text-gray-700" : "text-gray-400"}>{t}</span>
                    {n < 3 && <span className="w-4 h-px bg-surface-border" />}
                  </li>
                );
              })}
            </ol>

            <label
              onDragOver={(e) => { e.preventDefault(); if (!ocupado) setArrastando(true); }}
              onDragLeave={() => setArrastando(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArrastando(false);
                const f = e.dataTransfer.files?.[0];
                if (f && !ocupado) escolher(f);
              }}
              className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
                ocupado ? "opacity-60 cursor-wait" : "cursor-pointer hover:border-tm-400 hover:bg-tm-50/40"
              } ${arrastando ? "border-tm-500 bg-tm-50" : "border-surface-border"}`}
            >
              <span className="w-10 h-10 rounded-full bg-tm-50 text-tm-600 flex items-center justify-center">
                <IconDownload className="w-5 h-5 rotate-180" />
              </span>
              {arquivo ? (
                <span className="text-sm font-medium text-gray-800 break-all">{arquivo.name}</span>
              ) : (
                <span className="text-sm text-gray-700"><span className="font-semibold text-tm-700">Escolha o arquivo</span> ou arraste o PDF aqui</span>
              )}
              <span className="text-xs text-gray-400">
                {analisar.isPending ? "Lendo o arquivo…" : arquivo ? "Clique para trocar o arquivo" : "Somente PDF"}
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                disabled={ocupado}
                onChange={(e) => { escolher(e.target.files?.[0] ?? null); e.target.value = ""; }}
                className="sr-only"
              />
            </label>

            <p className="text-xs text-gray-500 flex gap-1.5">
              <IconInfo className="w-3.5 h-3.5 flex-shrink-0 mt-px text-gray-400" />
              Nada é gravado antes de você conferir a prévia. Reimportar a mesma fatura substitui os valores, não duplica.
            </p>

            {erro && (
              <div className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-lg p-3">{erro}</div>
            )}
          </div>
        </div>

        <div className="xl:col-span-3">
          <ChartCard title="Valor pago por fatura" subtitle="Total de cada fatura pelo vencimento, já com os créditos abatidos" height={250}
            loading={carregandoFaturas} isEmpty={serie.length === 0} emptyMessage="Nenhum extrato importado ainda.">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serie} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-toll-paid" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="venc" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(v: number) => `${(v / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}k`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                  labelFormatter={(_, p) => (p?.[0]?.payload ? `Fatura ${p[0].payload.fatura} · vence ${p[0].payload.venc}` : "")}
                  formatter={(v: number, n: string) => [brl(v), n === "pago" ? "Total pago" : "Créditos"]} />
                <Bar dataKey="pago" fill="url(#grad-toll-paid)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {previa && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <IconDocument className="w-4 h-4 text-gray-400" />
              <p className="text-sm font-semibold text-gray-900">Prévia da fatura {previa.invoiceNumber}</p>
              {previa.substituiu && <span className="badge-amber">Substitui uma fatura já importada</span>}
            </div>
            <p className="text-xs text-gray-500">
              Período coberto: {dia(previa.periodStart)} a {dia(previa.periodEnd)}
            </p>
          </div>

          <div className="p-5 space-y-4">
            {/* Problemas bloqueiam a gravação: um extrato que não fecha consigo
                mesmo viraria custo errado com cara de certo. */}
            {previa.problemas.length > 0 ? (
              <div className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-lg p-3">
                <p className="font-medium mb-1">Este arquivo não foi aceito:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {previa.problemas.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <PreviaStat label="Placas" value={previa.placas}
                  sub={<span title="Passagens com data lidas do detalhamento. É o que entra na margem, mês a mês">{previa.passagens} passagem(ns) datada(s)</span>} />
                <PreviaStat label="Subtotal" value={brl(previa.subtotal)} />
                <PreviaStat label="Total da nota" value={brl(previa.total)}
                  sub={previa.subtotal != null && previa.total != null && previa.subtotal > previa.total
                    ? `${brl(previa.subtotal - previa.total)} de créditos`
                    : undefined} />
                <PreviaStat label="Placas fora do cadastro" value={previa.placasSemCadastro.length}
                  tone={previa.placasSemCadastro.length ? "warning" : undefined} />
              </div>
            )}

            {previa.placasSemCadastro.length > 0 && (
              <div className="flex gap-2 rounded-lg border border-warning-100 bg-warning-50 px-3 py-2.5">
                <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-warning-700 leading-snug">
                  Estas placas estão no extrato mas não no cadastro do SinerSoft, então o valor delas
                  não vai aparecer em nenhum raio-X: <strong>{previa.placasSemCadastro.join(", ")}</strong>. O import
                  continua válido: o dado fica guardado e passa a aparecer quando a placa entrar no cadastro.
                </p>
              </div>
            )}

            {previa.problemas.length === 0 && (
              <>
                <div className="max-h-72 overflow-y-auto border border-surface-border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-muted sticky top-0">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-2 font-medium">Placa</th>
                        <th className="px-3 py-2 font-medium text-right">Pedágio</th>
                        <th className="px-3 py-2 font-medium text-right">Passagens</th>
                        <th className="px-3 py-2 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previa.itens.map((i) => (
                        <tr key={i.plate} className="border-t border-surface-border/70">
                          <td className="px-3 py-1.5">
                            {i.cadastrada ? (
                              <Link to={`/frota/placa/${encodeURIComponent(i.plate)}`} className="font-medium text-tm-700 hover:underline">{i.plate}</Link>
                            ) : (
                              i.plate
                            )}
                            {!i.cadastrada && (
                              <span className="ml-2 text-xs text-warning-600">fora do cadastro</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right">{brl(i.tolls)}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">{i.tollCount}</td>
                          <td className="px-3 py-1.5 text-right font-medium">{brl(i.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    disabled={ocupado || !arquivo || importar.isSuccess}
                    onClick={() => arquivo && importar.mutate(arquivo)}
                    className="btn-primary disabled:opacity-50"
                  >
                    {importar.isPending ? "Importando…" : "Confirmar importação"}
                  </button>
                  {importar.isSuccess ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-tm-700">
                      <IconCheck className="w-4 h-4" />
                      {previa.substituiu ? "Fatura atualizada" : "Fatura importada"}: {previa.placas} placa(s).
                    </span>
                  ) : (
                    <button type="button" className="btn-secondary" disabled={ocupado} onClick={() => escolher(null)}>
                      Cancelar
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border">
          <p className="text-sm font-semibold text-gray-900">Faturas já importadas</p>
          <p className="text-xs text-gray-400 mt-0.5">Total pago = subtotal − créditos. Esta tela não usa o filtro de período do topo.</p>
        </div>
        {carregandoFaturas ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
        ) : lista.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500 text-center">Nenhum extrato importado ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                  <th className="pl-5 pr-3 py-2 font-medium">Fatura</th>
                  <th className="px-3 py-2 font-medium">Período coberto</th>
                  <th className="px-3 py-2 font-medium">Vencimento</th>
                  <th className="px-3 py-2 font-medium text-right">Placas</th>
                  <th className="px-3 py-2 font-medium text-right">Subtotal</th>
                  <th className="px-3 py-2 font-medium text-right">Créditos</th>
                  <th className="px-3 py-2 font-medium text-right">Total pago</th>
                  <th className="pl-3 pr-5 py-2 font-medium text-right">Importada em</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {faturaList.visible.map((f) => {
                  const outros = outrosLancamentos(f);
                  return (
                    <tr key={f.id} className="text-gray-700 hover:bg-surface-muted/50">
                      <td className="pl-5 pr-3 py-2.5 font-medium text-gray-900 tabular-nums">{f.invoiceNumber}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{dia(f.periodStart)} a {dia(f.periodEnd)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{dia(f.dueDate)}</td>
                      <td className="px-3 py-2.5 text-right">{f.plates}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500 whitespace-nowrap">{brl(f.subtotal)}</td>
                      <td className="px-3 py-2.5 text-right text-tm-700 whitespace-nowrap">{f.credits ? `− ${brl(f.credits)}` : "—"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                        {brl(f.total)}
                        {outros !== 0 && (
                          <p className="text-[11px] font-normal text-gray-400" title="Diferença entre o total da nota e (subtotal − créditos)">
                            inclui {brl(Math.abs(outros))} de outros lançamentos
                          </p>
                        )}
                      </td>
                      <td className="pl-3 pr-5 py-2.5 text-right text-gray-400 whitespace-nowrap">
                        {new Date(f.importedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                    </tr>
                  );
                })}
                <LoadMoreRow list={faturaList} colSpan={8} label="faturas" capped={false} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
